import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth, SignedIn, SignedOut, RedirectToSignIn } from "@clerk/clerk-react";
import { apiGet, apiPost } from "../lib/api.js";

interface VendorDetail {
  id: string;
  name: string;
  category: string;
  description: string | null;
  website: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  quality_score: number | null;
  status: string;
  source: string;
  ai_generated: boolean;
  categories: Array<{ slug: string; is_primary: boolean; confidence: number | null }>;
  location: {
    address: string;
    city: string | null;
    region: string;
    country: string;
    lat: number | null;
    lng: number | null;
  } | null;
  items: Array<{
    id: string; title: string; description: string | null; category: string;
    price_cents: number | null; currency: string;
    price_display: "published" | "estimated" | "none";
    price_estimate_low_cents: number | null;
    price_estimate_high_cents: number | null;
    price_estimate_basis: string | null;
  }>;
  images: Array<{
    id: string;
    r2_key: string;
    source_url: string | null;
    matched_category: string | null;
    quality_score: number | null;
    relevance_score: number | null;
    gallery_category: string | null;
  }>;
}

// Builder-pickable gallery buckets (image_qa v15+). Order + couple-facing labels
// for the vendor-page gallery sections. Images with no/other category fall into
// the trailing "More photos" bucket (non-classified categories → one flat group).
const GALLERY_LABELS: Record<string, string> = {
  venue_space: "The space",
  wedding_event: "Real weddings",
  table_setting: "Table settings",
  catering: "Catering",
  bar: "Bar",
};
const GALLERY_ORDER = ["venue_space", "wedding_event", "table_setting", "catering", "bar"];
const OTHER_BUCKET = "_other";

const API_BASE = (): string =>
  (window as unknown as { __BRIDEPATH_API__?: string }).__BRIDEPATH_API__ ?? "";

export function VendorStore() {
  return (
    <>
      <SignedOut><RedirectToSignIn /></SignedOut>
      <SignedIn><VendorStoreInner /></SignedIn>
    </>
  );
}

function VendorStoreInner() {
  const { id } = useParams<{ id: string }>();
  const { getToken } = useAuth();
  const [vendor, setVendor] = useState<VendorDetail | null>(null);
  const [favorited, setFavorited] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      const [v, f] = await Promise.all([
        apiGet<VendorDetail>(`/vendors/${id}`, token),
        apiGet<{ favorited: boolean }>(`/favorites/vendor/${id}`, token),
      ]);
      if (cancelled) return;
      if (v.ok) setVendor(v.value); else setError(JSON.stringify(v.error));
      if (f.ok) setFavorited(f.value.favorited);
    })();
    return () => { cancelled = true; };
  }, [id, getToken]);

  const toggleFavorite = async () => {
    if (!id || favorited === null) return;
    const token = await getToken();
    if (!token) return;
    if (favorited) {
      // DELETE
      await fetch(`${API_BASE()}/favorites/vendor/${id}`, {
        method: "DELETE",
        headers: { authorization: `Bearer ${token}` },
      });
      setFavorited(false);
    } else {
      await apiPost(`/favorites`, token, { target_kind: "vendor", target_id: id });
      setFavorited(true);
    }
  };

  if (error) {
    return <section className="max-w-3xl mx-auto px-6 py-16 text-red-700 font-mono text-sm">{error}</section>;
  }
  if (!vendor) {
    return <section className="max-w-3xl mx-auto px-6 py-16 text-stone-500">Loading…</section>;
  }

  const hero = vendor.images[0];

  return (
    <section className="max-w-5xl mx-auto px-6 py-8">
      {/* Hero image */}
      {hero ? (
        <div className="rounded-lg overflow-hidden border border-stone-200 mb-6 max-h-[480px]">
          <AuthedImage
            src={`${API_BASE()}/admin/image/${hero.id}`}
            alt={vendor.name}
            className="w-full max-h-[480px] object-cover"
          />
        </div>
      ) : (
        <div className="rounded-lg bg-stone-100 h-64 mb-6 flex items-center justify-center text-stone-400">
          no images
        </div>
      )}

      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-4xl">{vendor.name}</h2>
          <div className="mt-2 text-sm text-stone-600 flex flex-wrap items-center gap-2">
            {vendor.location && (
              <span>{[vendor.location.city, vendor.location.region].filter(Boolean).join(", ")}</span>
            )}
            {vendor.location && <span className="text-stone-400">·</span>}
            <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs">
              unverified · unclaimed
            </span>
            {vendor.ai_generated && (
              <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-800 text-xs">
                AI-generated profile
              </span>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {vendor.categories.map((c) => (
              <span key={c.slug} className={`px-2 py-0.5 rounded-full text-xs ${c.is_primary ? "bg-stone-900 text-stone-50" : "bg-stone-100"}`}>
                {c.is_primary && "★ "}{c.slug}
              </span>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <button
            onClick={toggleFavorite}
            disabled={favorited === null}
            className={`px-4 py-2 rounded-full text-sm border transition ${favorited ? "bg-rose-100 border-rose-300 text-rose-800 hover:bg-rose-50" : "bg-white border-stone-300 hover:bg-stone-100"}`}
          >
            {favorited === null ? "…" : favorited ? "♥ Saved" : "♡ Save"}
          </button>
          {vendor.website && (
            <a
              href={vendor.website}
              target="_blank"
              rel="noreferrer"
              className="px-4 py-2 rounded-full text-sm bg-stone-900 text-stone-50 hover:bg-stone-700 text-center"
            >
              Visit website ↗
            </a>
          )}
        </div>
      </header>

      {vendor.description && (
        <p className="text-stone-700 mb-6 max-w-2xl">{vendor.description}</p>
      )}

      <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
        <strong>About this profile:</strong> BridePath built this listing from {vendor.website ?? "the vendor's website"} and AI.
        It hasn't been verified or claimed by the owner. Confirm details directly with the vendor before booking.
      </div>

      {vendor.location && (
        <section className="mb-6">
          <h3 className="text-xs uppercase tracking-wide text-stone-500 mb-2">location</h3>
          <div className="text-sm text-stone-700">
            <div>{vendor.location.address}</div>
            <div className="text-stone-500 text-xs mt-0.5">
              {[vendor.location.city, vendor.location.region, vendor.location.country].filter(Boolean).join(", ")}
            </div>
          </div>
        </section>
      )}

      {vendor.items.length > 0 && (
        <section className="mb-6">
          <h3 className="text-xs uppercase tracking-wide text-stone-500 mb-2">offerings</h3>
          <ul className="text-sm text-stone-700 divide-y divide-stone-100">
            {vendor.items.map((it) => (
              <li key={it.id} className="py-2 flex justify-between gap-3">
                <div>
                  <div className="font-medium">{it.title}</div>
                  {it.description && <div className="text-xs text-stone-500">{it.description}</div>}
                </div>
                <span className="font-mono text-stone-600 shrink-0 text-right">
                  {/* Estimated rows render their grounded RANGE (never the bare
                      midpoint stored in price_cents) with an honest label. */}
                  {it.price_display === "estimated" && it.price_estimate_low_cents != null && it.price_estimate_high_cents != null ? (
                    <>
                      ${(it.price_estimate_low_cents / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                      –${(it.price_estimate_high_cents / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                      {it.price_estimate_basis === "per_head" ? " / guest" : ""} {it.currency}
                      <span className="block text-[10px] text-stone-400 font-sans">estimated</span>
                    </>
                  ) : it.price_display === "published" && it.price_cents != null ? (
                    <>${(it.price_cents / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })} {it.currency}</>
                  ) : (
                    <span className="text-stone-400 italic">Price on request</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {vendor.images.length > 1 && (() => {
        // Bucket the gallery (everything past the hero) by gallery_category so a
        // venue's mixed gallery reads as "The space / Real weddings / Table
        // settings / Catering / Bar" instead of one undifferentiated grid. A
        // vendor with no classifications (non-venue, or pre-v15 images) collapses
        // to a single "Gallery" group — identical to the old flat layout.
        const rest = vendor.images.slice(1);
        const buckets = new Map<string, typeof rest>();
        for (const img of rest) {
          const key = img.gallery_category && GALLERY_LABELS[img.gallery_category] ? img.gallery_category : OTHER_BUCKET;
          const arr = buckets.get(key);
          if (arr) arr.push(img); else buckets.set(key, [img]);
        }
        const keys = [...GALLERY_ORDER.filter((k) => buckets.has(k)), ...(buckets.has(OTHER_BUCKET) ? [OTHER_BUCKET] : [])];
        // Single bucket (only "_other", or one category) → one plain "Gallery"
        // heading; multiple → a labeled section per category.
        const labelFor = (k: string): string =>
          k === OTHER_BUCKET ? (keys.length > 1 ? "More photos" : "gallery") : GALLERY_LABELS[k]!;
        return (
          <div className="space-y-6">
            {keys.map((k) => {
              const imgs = buckets.get(k)!;
              return (
                <section key={k}>
                  <h3 className="text-xs uppercase tracking-wide text-stone-500 mb-2">{labelFor(k)} ({imgs.length})</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {imgs.map((img) => (
                      <div key={img.id} className="rounded-lg overflow-hidden border border-stone-200">
                        <AuthedImage src={`${API_BASE()}/admin/image/${img.id}`} alt={img.gallery_category ?? img.matched_category ?? vendor.name} className="w-full aspect-square object-cover" />
                      </div>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        );
      })()}

      <footer className="mt-10 pt-6 border-t border-stone-100 text-xs text-stone-500">
        <Link to="/saved" className="underline">← Back to Saved</Link>
      </footer>
    </section>
  );
}

function AuthedImage({ src, alt, className }: { src: string; alt: string; className?: string }) {
  const { getToken } = useAuth();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let revoke: string | null = null;
    (async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      const res = await fetch(src, { headers: { authorization: `Bearer ${token}` } });
      if (!res.ok || cancelled) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      revoke = url;
      if (!cancelled) setBlobUrl(url);
    })();
    return () => { cancelled = true; if (revoke) URL.revokeObjectURL(revoke); };
  }, [src, getToken]);
  if (!blobUrl) return <div className={`${className ?? ""} bg-stone-100 flex items-center justify-center text-stone-400 text-xs`}>loading…</div>;
  return <img src={blobUrl} alt={alt} className={className} />;
}
